import { api } from './client';
import { i18nMetaMap, i18nArray } from '@/lib/i18n-map';
import '@/lib/i18n-map';

export const DEFAULT_CHART = 'Fișa principală';

export type ToothStatus =
  // the original thirteen
  | 'healthy'
  | 'cavity'
  | 'filling'
  | 'crown'
  | 'bridge'
  | 'veneer'
  | 'implant'
  | 'root_canal'
  | 'extracted'
  | 'missing'
  | 'monitored'
  | 'sealant'
  | 'post'
  // terapie
  | 'traumatic_defect'
  | 'non_carious'
  | 'deposits'
  | 'sensitivity'
  | 'fluorosis'
  | 'hypoplasia'
  | 'discoloration'
  | 'abrasion'
  | 'erosion'
  | 'root_resorption'
  // endodonție
  | 'pulpitis'
  | 'periodontitis'
  | 'broken_instrument'
  // parodontologie
  | 'gingivitis'
  | 'periodontal_disease'
  | 'mobility'
  // chirurgie
  | 'fracture'
  // protezare
  | 'inlay'
  | 'splinting'
  | 'denture'
  | 'artificial';

export type ToothStage = 'bolnav' | 'in_tratament' | 'tratat';

export const TOOTH_STAGE_META = i18nMetaMap<ToothStage, { label: string; color: string }>(
  'common',
  {
    bolnav: { label: 'bolnav', color: '#e0426f' },
    in_tratament: { label: 'inTratament', color: '#2f7fd8' },
    tratat: { label: 'tratat', color: '#1a9e5c' },
  },
);

export type ToothSpecialty =
  | 'terapie'
  | 'restaurare'
  | 'endodontie'
  | 'chirurgie'
  | 'parodontologie'
  | 'protezare'
  | 'ortodontie';

/**
 * The chart's catalogue, grouped as the specialists group it — the tabs of
 * the reference system. A status may serve two specialties (a crown is
 * prosthetics; a filling is restorative); the grouping is presentation, and
 * the API accepts any known key regardless of tab.
 */
export const TOOTH_SPECIALTY_GROUPS = i18nArray<{
  key: ToothSpecialty;
  label: string;
  statuses: ToothStatus[];
}>('common', [
  {
    key: 'terapie',
    label: 'terapie',
    statuses: [
      'healthy',
      'cavity',
      'traumatic_defect',
      'non_carious',
      'deposits',
      'sensitivity',
      'fluorosis',
      'hypoplasia',
      'discoloration',
      'abrasion',
      'erosion',
      'root_resorption',
      'monitored',
    ],
  },
  { key: 'restaurare', label: 'restaurare', statuses: ['filling', 'sealant', 'inlay'] },
  {
    key: 'endodontie',
    label: 'endodontie',
    statuses: ['pulpitis', 'periodontitis', 'root_canal', 'broken_instrument'],
  },
  {
    key: 'chirurgie',
    label: 'chirurgie',
    statuses: ['extracted', 'missing', 'implant', 'fracture'],
  },
  {
    key: 'parodontologie',
    label: 'parodontologie',
    statuses: ['gingivitis', 'periodontal_disease', 'mobility', 'deposits'],
  },
  {
    key: 'protezare',
    label: 'protezare',
    statuses: ['crown', 'bridge', 'veneer', 'post', 'inlay', 'splinting', 'denture', 'artificial'],
  },
  { key: 'ortodontie', label: 'ortodontie', statuses: ['monitored', 'mobility'] },
]);

export interface Tooth {
  tooth: number;
  /**
   * A comma-separated token list, not one state: `cavity,dx:Carie profundă,sf:MOD`.
   * A tooth carries several things at once, so read it with `parseTooth` from
   * `@/lib/tooth-tokens` rather than comparing it to a `ToothStatus`.
   */
  status: string;
  specialty: ToothSpecialty | null;
  stage: ToothStage | null;
  detail: string | null;
  note: string | null;
  treatedOn: string | null;
  doctor: string | null;
  finalized: boolean;
  /**
   * Cine a însemnat dintele întâia oară, și când.
   *
   * Opționale: serverul le trimite mereu, dar câteva ecrane își compun un
   * `Tooth` de mână — portalul, previzualizarea fișei 043/e — și pe acelea
   * proveniența nu le interesează.
   */
  createdBy?: string | null;
  createdAt?: string;
  updatedBy: string | null;
  updatedAt: string;
}

/** The server has accepted `cancelled` since `PHASE_STATUSES` got it (`clinical.dto.ts`). */
export type PhaseStatus = 'pending' | 'active' | 'done' | 'note' | 'cancelled';

export interface Phase {
  id: string;
  phase: string;
  description: string | null;
  status: PhaseStatus | null;
  teeth: string | null;
  cost: number | null;
  /** What the line was made at, before any reduction. */
  listCost: number | null;
  /** How much was taken off, as a percentage; null when nothing was. */
  discountPct: number | null;
  paidAmount: number;
  due: number;
  priority: string | null;
  doctor: string | null;
  planName: string | null;
  diagnostic: string | null;
  duration: number | null;
  labelDate: string | null;
  sortOrder: number;
  paidAt: string | null;
  /** Când s-a terminat lucrarea; null cât timp nu s-a terminat. */
  finishedAt: string | null;
  /** Serviciul din tarifar, când rândul a fost ales din el. */
  serviceId: string | null;
  /** Programarea pe care e fixat rândul, cât timp e programat. */
  appointmentId: string | null;
  /** Vizita la care s-a lucrat — legătura spre fișa 043/e. */
  visitId: string | null;
  /** Medicul care a făcut lucrarea, ca id. */
  doctorId: string | null;
  /** Implantul simulat pe CBCT din care a venit rândul, când a venit dintr-unul. */
  studyObjectId: string | null;
  /** Contractul plătitorului persoană juridică (CNAM, asigurător, firmă); null = pacientul. */
  payerContractId: string | null;
  /** Cât din cost plătește acel plătitor; null fără contract. */
  payerShare: number | null;
  createdBy: string | null;
  updatedBy: string | null;
  /** Versiunea rândului: se trimite înapoi ca `expectedUpdatedAt`, ca o editare veche să fie refuzată. */
  updatedAt: string;
  /** Când a fost făcut rândul. Lipsește la răspunsurile vechi. */
  createdAt?: string;
}

/** Ce trimite o modificare de rând: câmpurile, plus versiunea citită. */
export type UpdatePhaseInput = Partial<Omit<Phase, 'updatedAt'>> & { expectedUpdatedAt?: string };

export interface ClinicalRecord {
  patient: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    dob: string | null;
    gender: string | null;
    bloodType: string | null;
    allergies: string[];
    doctorId: string | null;
    alert: string | null;
    notes: string | null;
    advance: number;
    /** Registration number of form 043/e; null until the first generation. */
    fisa043No: string | null;
    fisa043Versions: Fisa043Version[];
    /** Textul scris de medic pentru zilele din „Zilnic", pe `zz.ll.aaaa`. */
    fisa043Journal: Record<string, string>;
    /** Names of the plans the patient has chosen. */
    acceptedPlans: string[];
    /** The plans the patient turned down, newest first — acceptance's counterpart. */
    declinedPlans: DeclinedPlan[];
    accessStatus: string | null;
    lastVisit: string | null;
  };
  charts: Record<string, Tooth[]>;
  phases: Phase[];
  summary: {
    /** The treatment plan's own figures: what it costs, what has been put against it. */
    planned: number;
    /** The cost of the plan rows already done. */
    plannedDone: number;
    paid: number;
    /**
     * What the clinic charged and what it took, across everything — the diary
     * included. A patient billed without a treatment plan has no plan rows,
     * so `planned` and `paid` are 0 for them while the payment history is
     * twenty-two receipts long; these two are what that history adds up to.
     * `billed` counts finished visits only, never a booking still to come.
     */
    /**
     * `null` for a reader without `incasari.vede`: the patient's account is not
     * sent to someone whose screen does not show it.
     */
    billed: number | null;
    collected: number | null;
    due: number | null;
    /**
     * Where `due` came from. While a payment plan is active the debt is the
     * plan's and the procedures under it are not counted as well — the same
     * rule Achitări applies, so the two screens agree.
     */
    dueSource: 'plan' | 'proceduri';
  };
  prescriptions: Array<{
    id: string;
    name: string;
    instructions: string | null;
    prescribedOn: string | null;
    doctor: string | null;
    active: boolean;
  }>;
  notes: Array<{
    id: string;
    noteDate: string;
    text: string;
    mood: string | null;
    authorName: string | null;
    createdAt: string;
    /** The note this one corrects. */
    correctsNoteId: string | null;
    /** Set when a later note corrected this one; the text stays. */
    correctedAt: string | null;
  }>;
  alerts: Array<{
    id: string;
    alertType: string;
    text: string;
    alertDate: string | null;
    doctor: string | null;
  }>;
  documents: Array<{
    id: string;
    name: string;
    docType: string | null;
    filePath: string | null;
    sizeLabel: string | null;
    category: string | null;
    docDate: string | null;
  }>;
  images: Array<{
    id: string;
    name: string | null;
    filePath: string | null;
    category: string | null;
    imgDate: string | null;
    /** Numere FDI prin virgulă, ca la planuri și la laborator. */
    teeth: string | null;
    /** Acordul pacientului pentru fotografie (etapa 9); null = nu s-a întrebat. */
    photoConsent: 'acordat' | 'refuzat' | null;
    photoPurposes: PhotoPurpose[] | null;
  }>;
}

/** Pentru ce își dă pacientul acordul pe o fotografie. */
export type PhotoPurpose = 'clinic' | 'predare' | 'publicitate';

export interface OpenPhase {
  id: string;
  phase: string;
  /** Planul acceptat din care vine; doar acelea se programează. */
  planName: string | null;
  teeth: string | null;
  diagnostic: string | null;
  cost: number | null;
  status: string;
  duration: number | null;
  /** Where the row is booked, when a booking still holds it. */
  booking: { appointmentId: string; date: string; time: string; status: string } | null;
}

/** One registered generation of form 043/e. */
export interface Fisa043Version {
  at: string;
  by: string;
  sign: string;
  consent: boolean;
  /** Câte zile avea rubrica „Zilnic" când s-a tipărit. */
  entries?: number;
  /** Vizita pentru care s-a generat, când s-a generat dintr-o vizită. */
  visitId?: string | null;
  /** Ziua acelei vizite. */
  visitDate?: string | null;
  /** Formula dentară așa cum era atunci — o versiune veche se retipărește ca atunci. */
  teeth?: Array<{ tooth: number; status: string | null }>;
  /** Numărul fișei; lipsește la generările dinainte de numerotarea pe fișă. */
  no?: string;
  /** Rândul fișei, care ține PDF-ul păstrat. */
  sheetId?: string;
}

/** O fișă 043/e generată, cu numărul ei unic în clinică și PDF-ul păstrat la generare. */
/** Un raport de imagistică semnat, cum îl tipărește fișa 043/e. */
export interface Fisa043ImagingReport {
  studyId: string;
  kind: string;
  studyDate: string;
  version: number;
  summary: string | null;
  recommendation: string | null;
  findings: Array<{ tooth: number | null; label: string; severity: string | null }>;
  /** Comentariile medicului pe dinți, din versiunea semnată. */
  toothComments?: Array<{ tooth: number; comment: string }>;
  signedAt: string;
  signedName: string | null;
}

export interface Fisa043Sheet {
  id: string;
  no: string;
  /** Numărul tipărit pe hârtie, la o fișă generată înainte de numerotarea pe fișă. */
  printedNo: string | null;
  visitId: string | null;
  visitDate: string | null;
  sign: string;
  consent: boolean;
  entries: number;
  generatedAt: string;
  generatedBy: string | null;
  hasPdf: boolean;
  pdfSize: number | null;
}

export interface PortalMessageRow {
  id: string;
  fromRole: 'patient' | 'staff';
  senderName: string | null;
  text: string;
  read: boolean;
  createdAt: string;
}

/** O vizită, așa cum o vede tabul Tratament: zi și stare, fără textele fișei 043/e. */
export interface TreatmentVisit {
  id: string;
  visitDate: string;
  status: 'draft' | 'completed';
  appointmentId: string | null;
  doctorName: string | null;
}

/** Un plan refuzat de pacient: motivul (dacă l-a spus), ziua și cine l-a notat. */
export interface DeclinedPlan {
  name: string;
  reason: string | null;
  at: string;
  by: string | null;
}

/** Ce desenează tabul Tratament — și nimic din restul fișei clinice. */
export interface TreatmentView {
  patient: { id: string; name: string; acceptedPlans: string[]; declinedPlans: DeclinedPlan[] };
  teeth: Tooth[];
  phases: Phase[];
  visits: TreatmentVisit[];
}

/** `PATCH /patients/:id/record/phases` — mirrors `UpdatePhasesDto`. */
export interface UpdatePhasesInput {
  ids: string[];
  doctor?: string | null;
  priority?: string | null;
  /** Versiunile citite; una depășită refuză tot cu 409. */
  versions?: Array<{ id: string; updatedAt: string }>;
}

export const clinicalApi = {
  /** Tabul Tratament; îl citește și recepția, prin `tratament.finalizeaza`. */
  treatment: (patientId: string) =>
    // O cerere agățată lăsa scheletul gri la nesfârșit (T7).
    api.get<TreatmentView>(`/patients/${patientId}/record/treatment`, { timeoutMs: 20_000 }),
  /**
   * The clinic's side of a patient's portal thread. Opening it marks what the
   * patient wrote as read — reading is the acknowledgement, and a separate
   * button for it would only ever be pressed by accident.
   */
  messages: (patientId: string) => api.get<PortalMessageRow[]>(`/patients/${patientId}/messages`),
  reply: (patientId: string, text: string) =>
    api.post<PortalMessageRow[]>(`/patients/${patientId}/messages`, { text }),

  openPhases: (patientId: string) =>
    api.get<OpenPhase[]>(`/patients/${patientId}/record/phases/open`),
  record: (patientId: string) => api.get<ClinicalRecord>(`/patients/${patientId}/record`),

  /** Mark a named plan as chosen by the patient, or undo that. */
  setPlanAccepted: (patientId: string, planName: string, accepted: boolean) =>
    api.post<{ acceptedPlans: string[] }>(`/patients/${patientId}/record/plans/accept`, {
      planName,
      accepted,
    }),

  /** The patient turned the plan down (with a reason, if they gave one), or reopen it. */
  setPlanDeclined: (
    patientId: string,
    planName: string,
    declined: boolean,
    reason: string | null = null,
  ) =>
    api.post<{ acceptedPlans: string[]; declinedPlans: DeclinedPlan[] }>(
      `/patients/${patientId}/record/plans/decline`,
      { planName, declined, ...(declined && reason ? { reason } : {}) },
    ),

  /** Take a percentage off a plan; no row falls below what was paid on it. */
  renamePlan: (patientId: string, planName: string, newName: string) =>
    api.post<{ planName: string; rows: number }>(`/patients/${patientId}/record/plans/rename`, {
      planName,
      newName,
    }),

  duplicatePlan: (patientId: string, planName: string, newName?: string) =>
    api.post<{ planName: string; rows: number }>(`/patients/${patientId}/record/plans/duplicate`, {
      planName,
      ...(newName ? { newName } : {}),
    }),
  /** A new plan from ticked rows, which keep their prices; the plans they come from stay. */
  planFromRows: (patientId: string, phaseIds: string[], planName: string) =>
    api.post<{ planName: string; rows: number }>(`/patients/${patientId}/record/plans/from-rows`, {
      phaseIds,
      planName,
    }),

  deletePlan: (patientId: string, planName: string, reason?: string) =>
    api.post<{ removed: number }>(`/patients/${patientId}/record/plans/delete`, {
      planName,
      ...(reason ? { reason } : {}),
    }),

  discountPlan: (patientId: string, planName: string, percent: number) =>
    api.post<{
      applied: number;
      before: number;
      after: number;
      floored: Array<{ phase: string; paid: number }>;
    }>(`/patients/${patientId}/record/plans/discount`, { planName, percent }),

  /** Register a generation; the card number is assigned on the first one. */
  /** Generează o fișă 043/e: fiecare generare primește numărul ei, unic în clinică. */
  generateFisa043: (
    patientId: string,
    body: { sign: string; consent: boolean; entries?: number; visitId?: string | null },
  ) =>
    api.post<{ no: string; sheetId: string; versions: Fisa043Version[] }>(
      `/patients/${patientId}/record/fisa-043`,
      body,
    ),

  /** Fișele 043/e generate ale pacientului, cea mai nouă sus. */
  fisa043Sheets: (patientId: string) =>
    api.get<Fisa043Sheet[]>(`/patients/${patientId}/record/fisa-043/sheets`),

  /** Rapoartele de imagistică semnate pe care le tipărește fișa 043/e. */
  /** Formula dentară pe care o tipărește 043/e: din ziua vizitei, sau a primei vizite finalizate. */
  fisa043Chart: (patientId: string, visitId: string | null) =>
    api.get<{ date: string | null; teeth: Array<{ tooth: number; status: string | null }> }>(
      `/patients/${patientId}/record/fisa-043/chart`,
      { query: visitId ? { visitId } : undefined },
    ),

  fisa043Imaging: (patientId: string) =>
    api.get<Fisa043ImagingReport[]>(`/patients/${patientId}/record/fisa-043/imaging`),

  /** Păstrează PDF-ul făcut la generare — o singură dată; a doua oară serverul refuză. */
  saveFisa043Pdf: (patientId: string, sheetId: string, pdf: Blob) => {
    const form = new FormData();
    form.append('file', pdf, 'fisa-043.pdf');
    return api.post<Fisa043Sheet>(`/patients/${patientId}/record/fisa-043/${sheetId}/pdf`, form);
  },

  /** Un link de scurtă durată la PDF-ul păstrat, de deschis într-o filă. */
  fisa043PdfUrl: (patientId: string, sheetId: string) =>
    api.get<{ url: string }>(`/patients/${patientId}/record/fisa-043/${sheetId}/pdf-url`),

  /** PDF-ul păstrat, descărcat cu numele fișei. */
  downloadFisa043Pdf: (patientId: string, sheetId: string, no: string) =>
    api.download(`/patients/${patientId}/record/fisa-043/${sheetId}/pdf`, `fisa-043-nr-${no}.pdf`),

  /** Textul medicului pentru o zi din „Zilnic"; gol înseamnă „întoarce-te la rezumat". */
  saveFisa043Journal: (patientId: string, body: { date: string; text: string | null }) =>
    api.post<Record<string, string>>(`/patients/${patientId}/record/fisa-043/journal`, body),

  setTooth: (
    patientId: string,
    body: {
      tooth: number;
      /** The token list; see `Tooth.status`. */
      status: string;
      chartName?: string;
      specialty?: ToothSpecialty | null;
      stage?: ToothStage | null;
      detail?: string | null;
      note?: string | null;
      doctor?: string | null;
      finalized?: boolean;
    },
  ) => api.post<{ ok: boolean }>(`/patients/${patientId}/record/teeth`, body),

  clearTooth: (patientId: string, tooth: number, chart?: string) =>
    api.delete<void>(`/patients/${patientId}/record/teeth/${tooth}`, { query: { chart } }),

  /**
   * A chart is the name on a set of teeth, not a row of its own — so it comes
   * into being when the first tooth is recorded under a new name, and these
   * two are the only operations it needs.
   */
  renameChart: (patientId: string, from: string, to: string) =>
    api.post<{ ok: boolean }>(`/patients/${patientId}/record/charts/rename`, { from, to }),

  removeChart: (patientId: string, name: string) =>
    api.delete<void>(`/patients/${patientId}/record/charts/${encodeURIComponent(name)}`),

  addPhase: (patientId: string, body: Partial<Phase>) =>
    api.post<Phase>(`/patients/${patientId}/record/phases`, body),

  updatePhase: (patientId: string, id: string, body: UpdatePhaseInput) =>
    api.patch<Phase>(`/patients/${patientId}/record/phases/${id}`, body),

  deletePhase: (patientId: string, id: string, reason?: string) =>
    api.delete<void>(`/patients/${patientId}/record/phases/${id}`, { body: { reason } }),

  /** Ordinea rândurilor din plan, de sus în jos. */
  reorderPhases: (patientId: string, ids: string[]) =>
    api.post<{ ok: boolean }>(`/patients/${patientId}/record/phases/reorder`, { ids }),

  /** Același medic sau aceeași urgență pe mai multe rânduri deodată — cele bifate. */
  updatePhases: (patientId: string, body: UpdatePhasesInput) =>
    api.patch<{ ok: boolean; updated: number }>(`/patients/${patientId}/record/phases`, body),

  addNote: (patientId: string, body: { text: string; mood?: string | null }) =>
    api.post(`/patients/${patientId}/record/notes`, body),

  /** A correction is a new note; the corrected one stays, marked. */
  correctNote: (patientId: string, noteId: string, text: string) =>
    api.post(`/patients/${patientId}/record/notes/${noteId}/correct`, { text }),

  addAlert: (patientId: string, body: { text: string; alertType?: string }) =>
    api.post(`/patients/${patientId}/record/alerts`, body),

  dismissAlert: (patientId: string, id: string) =>
    api.delete<void>(`/patients/${patientId}/record/alerts/${id}`),
};

/** How each tooth state is drawn and named. */
/**
 * Statuses that describe something wrong — what the "Probleme din fișa
 * dentară" strip on the treatment plan counts. Treatments (filling, crown)
 * and states (healthy, missing) are not problems.
 */
export const DISEASE_STATUSES: ReadonlySet<string> = new Set([
  'cavity',
  'traumatic_defect',
  'non_carious',
  'deposits',
  'fluorosis',
  'hypoplasia',
  'discoloration',
  'abrasion',
  'erosion',
  'root_resorption',
  'pulpitis',
  'periodontitis',
  'broken_instrument',
  'gingivitis',
  'periodontal_disease',
  'mobility',
  'fracture',
]);

/**
 * How each state is drawn: its Romanian name, its colour, and the letter the
 * chart puts on the tooth.
 *
 * The colours are the reference system's, and are tokens rather than hex
 * values so a state answers to the theme: `cavity` was `C.bad` there and is
 * `var(--danger)` here, `filling` was `C.info`, `crown` and `bridge` were
 * `C.warn`, `veneer` and `monitored` were `C.ok`, `root_canal` (its
 * `endodonție`) was `C.alt`. Two states keep a literal because they had one
 * there too — implant's orange and pivot's purple sit deliberately outside
 * the status palette, so a tooth carrying either is not read as a warning.
 *
 * The states after `post` are the ones form 043/e adds; they take the tone of
 * the diagnosis group they belong to in DX_CATALOG, so a chart and the
 * diagnosis list under it agree about what is urgent.
 *
 * `healthy` is the exception: it is the absence of a marking rather than a
 * colour, so it resolves to the surface underneath.
 *
 * Checked against the old source by `node scripts/check-parity.mjs`.
 */
export const TOOTH_STATUS_META = i18nMetaMap<
  ToothStatus,
  { label: string; color: string; short: string }
>('common', {
  healthy: { label: 'sanatos', color: 'var(--surface)', short: '' },
  cavity: { label: 'carie', color: 'var(--danger)', short: 'C' },
  filling: { label: 'obturatie', color: 'var(--info)', short: 'O' },
  crown: { label: 'coroana', color: 'var(--warn)', short: 'Co' },
  bridge: { label: 'punte', color: 'var(--warn)', short: 'P' },
  veneer: { label: 'fateta', color: 'var(--ok)', short: 'F' },
  implant: { label: 'implant', color: '#ea580c', short: 'I' },
  root_canal: { label: 'tratamentDeCanal', color: 'var(--alt)', short: 'E' },
  extracted: { label: 'extras', color: 'var(--danger)', short: 'X' },
  missing: { label: 'absent', color: 'var(--text-subtle)', short: '–' },
  monitored: { label: 'monitorizat', color: 'var(--ok)', short: 'M' },
  sealant: { label: 'sigilare', color: 'var(--info)', short: 'S' },
  post: { label: 'pivot', color: '#9333ea', short: 'Pv' },
  // terapie
  traumatic_defect: { label: 'defectTraumatic', color: 'var(--warn)', short: 'DT' },
  non_carious: { label: 'afectiuneNecarioasa', color: 'var(--warn)', short: 'AN' },
  deposits: { label: 'depuneri', color: 'var(--danger)', short: 'Dp' },
  sensitivity: { label: 'sensibilitate', color: 'var(--warn)', short: 'Sb' },
  fluorosis: { label: 'fluoroza', color: 'var(--warn)', short: 'Fl' },
  hypoplasia: { label: 'hipoplazie', color: 'var(--warn)', short: 'Hp' },
  discoloration: { label: 'decolorare', color: 'var(--text-muted)', short: 'Dc' },
  abrasion: { label: 'abraziune', color: 'var(--warn)', short: 'Ab' },
  erosion: { label: 'eroziune', color: 'var(--warn)', short: 'Er' },
  root_resorption: { label: 'resorbtieRadiculara', color: 'var(--danger)', short: 'RR' },
  // endodonție
  pulpitis: { label: 'pulpita', color: 'var(--danger)', short: 'Pu' },
  periodontitis: { label: 'periodontita', color: 'var(--danger)', short: 'Pd' },
  broken_instrument: { label: 'instrumentRuptInCanal', color: 'var(--danger)', short: 'IR' },
  // parodontologie
  gingivitis: { label: 'gingivita', color: 'var(--danger)', short: 'Gv' },
  periodontal_disease: { label: 'parodontita', color: 'var(--danger)', short: 'Pa' },
  mobility: { label: 'mobilitate', color: 'var(--warn)', short: 'Mo' },
  // chirurgie
  fracture: { label: 'fractura', color: 'var(--danger)', short: 'Fr' },
  // protezare
  inlay: { label: 'incrustatie', color: 'var(--info)', short: 'In' },
  splinting: { label: 'sinare', color: 'var(--info)', short: 'Și' },
  denture: { label: 'proteza', color: 'var(--info)', short: 'Pr' },
  artificial: { label: 'artificial', color: 'var(--text-muted)', short: 'Ar' },
});

/**
 * The official codes of the dental formula in form 043/e.
 *
 * Separate from `TOOTH_STATUS_META.short` on purpose: `short` is ours, chosen
 * to read well on a coloured chart, while these are fixed by the ministry's
 * form and must not drift to suit a screen. Where the form has no code for a
 * state this system records, the clinic's own notation is used — marked as
 * such in the legend below.
 *
 * `A` genuinely serves two meanings in the printed form (parodontoză and
 * artificial tooth); that ambiguity is the form's, and is reproduced rather
 * than corrected.
 */
/**
 * Ce se scrie în căsuța dintelui, pe formula dentară.
 *
 * Erau literele formularului, și trei dintre ele însemnau două lucruri: `A`
 * era și parodontoză, și dinte artificial, și depuneri; `T` era și fractură,
 * și defect traumatic; `Ex` însemna „extracție indicată" în legendă și „extras"
 * în hartă. Restul se citeau numai cu legenda alături — `Pl`, `Pv`, `Pt`,
 * `Pa`, `In`, `Nc`, `Rr`, `E!`.
 *
 * Acum fiecare stare are un cuvânt scurtat din numele ei, de cel mult cinci
 * litere, cât intră în căsuță; o căsuță goală înseamnă dinte nenotat, iar
 * legenda de sub formulă se scrie din codurile chiar folosite pe fișa aceea.
 */
export const FISA_TOOTH_CODE: Partial<Record<ToothStatus, string>> = {
  healthy: 'Norm',
  cavity: 'Car',
  filling: 'Obt',
  root_canal: 'Endo',
  crown: 'Cor',
  bridge: 'Pun',
  veneer: 'Faț',
  post: 'Piv',
  implant: 'Imp',
  extracted: 'Extr',
  missing: 'Abs',
  sealant: 'Sig',
  fracture: 'Fract',
  mobility: 'Mob',
  monitored: 'Obs',
  pulpitis: 'Pulp',
  periodontitis: 'Perio',
  gingivitis: 'Ging',
  periodontal_disease: 'Paro',
  deposits: 'Dep',
  artificial: 'Artif',
  traumatic_defect: 'Traum',
  sensitivity: 'Sens',
  hypoplasia: 'Hipo',
  erosion: 'Eroz',
  abrasion: 'Abraz',
  discoloration: 'Decol',
  fluorosis: 'Fluor',
  non_carious: 'Necar',
  root_resorption: 'Resor',
  broken_instrument: 'Instr',
  inlay: 'Incr',
  splinting: 'Șină',
  denture: 'Prot',
};

/**
 * Codurile chiar folosite pe o fișă, cu ce înseamnă fiecare.
 *
 * O legendă de treizeci de notații se citește o dată și niciodată; cele cinci
 * care apar pe fișa din față se citesc de fiecare dată. Ordinea e alfabetică,
 * ca să se găsească un cod fără să fie căutat prin toată linia.
 */
export function toothCodesUsed(
  teeth: Array<{ status: string | null }>,
): Array<{ code: string; label: string }> {
  const seen = new Map<string, string>();
  for (const t of teeth) {
    for (const token of String(t.status ?? '').split(',')) {
      const key = token.trim() as ToothStatus;
      const code = FISA_TOOTH_CODE[key];
      if (code && !seen.has(code)) seen.set(code, TOOTH_STATUS_META[key].label);
    }
  }
  return [...seen]
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.code.localeCompare(b.code, 'ro'));
}

/** The conventional signs, worded as the ministry's form words them. */
export const FISA_LEGEND_RO =
  'lipseşte – L, rădăcină – R, carie – C, pulpită – P, periodontită – Pt, ' +
  'plombat – Pl, parodontoză – A, mobilitate – I, II, III (grad), coroană – Cr, ' +
  'dinte artificial – A';

export const FISA_LEGEND_RU =
  'отсутствует – L, корень – R, кариес – C, пульпит – P, периодонтит – Pt, ' +
  'пломбированный – Pl, пародонтоз – A, подвижность – I, II, III (степень), ' +
  'коронка – Cr, искусственный зуб – A';

/**
 * The code printed under one tooth on the ministry's form.
 *
 * The form has one cell per tooth, so a tooth carrying several states has to
 * pick. It prints the first one recorded, which is the order the clinician
 * marked them in — the alternative, printing a joined list, does not fit the
 * box and is not what the form asks for.
 *
 * Reads the token list rather than the raw column: comparing the whole string
 * to a code left every tooth with more than one state printing an empty cell,
 * which on a signed medical form reads as "nothing found".
 */
export function fisaToothCode(status: string | null | undefined): string {
  if (!status) return '';
  for (const token of String(status).split(',')) {
    const code = FISA_TOOTH_CODE[token.trim() as ToothStatus];
    if (code) return code;
  }
  return '';
}

/**
 * FDI quadrants for the permanent dentition.
 *
 * Upper right, upper left, lower left, lower right — the order they are drawn
 * in, which is how a dentist reads a chart facing the patient.
 */
export const FDI_QUADRANTS = {
  upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
  upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
  lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
  lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
} as const;

/** FDI quadrants for the primary dentition, drawn the same way. */
export const FDI_PRIMARY_QUADRANTS = {
  upperRight: [55, 54, 53, 52, 51],
  upperLeft: [61, 62, 63, 64, 65],
  lowerLeft: [71, 72, 73, 74, 75],
  lowerRight: [85, 84, 83, 82, 81],
} as const;

/**
 * Whether a number is a tooth: 11–48 up to 8 per quadrant, 51–85 up to 5.
 * The API checks the same set (`FDI_TEETH`); a range lets 19 or 56 through.
 */
export function isFdiTooth(n: number): boolean {
  if (!Number.isInteger(n)) return false;
  const quadrant = Math.floor(n / 10);
  const position = n % 10;
  if (quadrant >= 1 && quadrant <= 4) return position >= 1 && position <= 8;
  if (quadrant >= 5 && quadrant <= 8) return position >= 1 && position <= 5;
  return false;
}
